import React, { createContext, useState, useEffect } from 'react';
import _ from 'lodash';
import uuid from 'uuid';

import {
    LikertScaleQuestion,
    MultiSelectQuestion,
    RankingQuestion,
    SingleSelectQuestion,
} from 'components/organisms/Survey';
import { useAuth } from 'contexts/auth';
import { useUser } from 'contexts/user';
import courseRequests from 'services/course';
import { COUNTRY_LIST, EN_PROFICIENCY_OPTIONS, PRONOUN_OPTIONS, TIME_ZONE_LIST } from 'utils/constants';
import { findDefaultProfile } from 'utils/helper';

const PageContext = createContext();

const STEP_KEYS = {
    SURVEY: 'survey',
    FINISHED_SURVEY: 'finished-survey',
};

const FIELD_TYPES = {
    SINGLE_SELECT: 'single-select',
    MULTI_SELECT: 'multi-select',
    LIKERT_SCALE: 'likert-scale',
    RANK: 'rank',
};

const PageProvider = (props) => {
    const { auth, currentUser, changeAccount } = useAuth();
    const { course } = useUser();
    const [loading, setLoading] = useState(false);
    const [questions, setQuestions] = useState();
    const [canSubmit, setCanSubmit] = useState(true);
    const [currentStep, setCurrentStep] = useState();
    const [survey, setSurvey] = useState();
    const [review, setReview] = useState(false);

    const defaultCriteria = ['time-zone', 'gender', 'country', 'english-proficiency'];

    const handleSubmit = async () => {
        const groupFormation = await courseRequests.listCourseGroupFormations(course.id);
        const surveyData = {
            ...survey,
            owner_account_id: auth.owner_account_id,
            user_id: auth.user_id,
            course_id: course.id,
            group_formation_id: groupFormation.id,
        };
        setReview(true);
        if (survey.owner_account_id) {
            return courseRequests.updateCourseGroupSurvey(course.id, surveyData);
        }

        await courseRequests.createCourseSurvey(course.id, surveyData);
        return setSurvey(surveyData);
    };

    const handleClickNext = () => {
        setCurrentStep(STEP_KEYS.FINISHED_SURVEY);
        return !review && handleSubmit();
    };

    const handleClickBack = () => {
        setCurrentStep(STEP_KEYS.SURVEY);
    };

    const handleAnswer = (fieldType, id, value) => {
        if (!survey) return;
        let newSurveyData = survey;
        const findCriterion = newSurveyData?.criteria?.find((criterion) => criterion.id === id);
        switch (fieldType) {
            case FIELD_TYPES.SINGLE_SELECT: {
                if (findCriterion) {
                    newSurveyData.criteria = newSurveyData?.criteria?.map((criterion) => {
                        if (criterion.id === id) {
                            return {
                                id,
                                value: { key: value.key, value: value.value },
                            };
                        }
                        return criterion;
                    });
                } else {
                    newSurveyData.criteria = [...newSurveyData?.criteria, { id, value }];
                }
                break;
            }
            case FIELD_TYPES.MULTI_SELECT: {
                if (findCriterion) {
                    newSurveyData.criteria = newSurveyData?.criteria?.map((criterion) => {
                        if (criterion.id === id) {
                            return {
                                id,
                                value,
                            };
                        }
                        return criterion;
                    });
                } else {
                    newSurveyData.criteria = [...newSurveyData?.criteria, { id, value }];
                }
                break;
            }
            case FIELD_TYPES.LIKERT_SCALE: {
                if (findCriterion) {
                    newSurveyData.criteria = newSurveyData?.criteria?.map((criterion) => {
                        if (criterion.id === id) {
                            criterion = {
                                id,
                                value: { key: value.key, value: value.value, number: value.number },
                            };
                            return criterion;
                        }
                        return criterion;
                    });
                } else {
                    newSurveyData.criteria = [...newSurveyData?.criteria, { id, value, number: value.number }];
                }
                break;
            }
            case FIELD_TYPES.RANK: {
                if (findCriterion) {
                    newSurveyData.criteria = newSurveyData?.criteria?.map((criterion) => {
                        if (criterion.id === id) {
                            criterion = {
                                id,
                                value,
                            };
                            return criterion;
                        }
                        return criterion;
                    });
                } else {
                    newSurveyData.criteria = [...newSurveyData?.criteria, { id, value }];
                }
                break;
            }
            default:
                break;
        }
        return setSurvey(Object.assign({}, newSurveyData));
    };

    const handleClickToReview = () => {
        return setCurrentStep(STEP_KEYS.SURVEY);
    };

    const handleClickToCourseDashboard = () => {
        return changeAccount();
    };

    const renderQuestionType = (data, index) => {
        const surveyValue = survey?.criteria.find((criterion) => criterion.id === data.id)?.value;
        const title = data?.custom_question || data?.name;
        const findDefaultItem =
            !surveyValue && defaultCriteria.includes(data.id) ? findDefaultProfile(data.id, currentUser) : surveyValue;
        switch (data.field_type) {
            case 'single-select': {
                return (
                    <SingleSelectQuestion
                        key={data.id}
                        id={data.id}
                        defaultItem={findDefaultItem}
                        question={`${index + 1}. ${title}`}
                        itemList={[{ key: '', value: 'Select' }, ...data.options] || []}
                        handleSelected={(value) => handleAnswer(FIELD_TYPES.SINGLE_SELECT, data.id, value)}
                        review={review}
                    />
                );
            }

            case 'multi-select': {
                return (
                    <MultiSelectQuestion
                        key={data.id}
                        id={data.id}
                        defaultItem={findDefaultItem}
                        question={`${index + 1}. ${title}`}
                        itemList={data.options || []}
                        handleSelected={(value) => handleAnswer(FIELD_TYPES.MULTI_SELECT, data.id, value)}
                        review={review}
                    />
                );
            }

            case 'likert-scale': {
                return (
                    <LikertScaleQuestion
                        key={data.id}
                        id={data.id}
                        defaultItem={findDefaultItem}
                        question={`${index + 1}. ${title}`}
                        itemList={data.options || []}
                        handleSelected={(value) => handleAnswer(FIELD_TYPES.LIKERT_SCALE, data.id, value)}
                        review={review}
                    />
                );
            }

            case 'rank': {
                return (
                    <RankingQuestion
                        key={data.id}
                        defaultItem={findDefaultItem}
                        question={`${index + 1}. ${title}`}
                        itemList={data.options || []}
                        review={review}
                        setCanSubmit={setCanSubmit}
                        handleSelected={(value) => handleAnswer(FIELD_TYPES.RANK, data.id, value)}
                    />
                );
            }
        }
    };

    const renderQuestions = (isDefault) => {
        const defaultQuestionNumbers = questions?.filter(
            (question) => question.show && defaultCriteria.includes(question.id)
        )?.length;

        return questions
            ?.filter((question) =>
                question.show && isDefault
                    ? defaultCriteria.includes(question.id)
                    : !defaultCriteria.includes(question.id)
            )
            .map((question, index) => {
                const questionNumber = isDefault ? index : index + defaultQuestionNumbers;
                return renderQuestionType(question, questionNumber);
            });
    };

    useEffect(() => {
        if (auth && course && !questions) {
            const getData = async () => {
                setLoading(true);
                const courseGroupFormation = await courseRequests.listCourseGroupFormations(course.id);
                const findMySurvey = await courseRequests.listCourseGroupSurveys(
                    course.id,
                    JSON.stringify({
                        owner_account_id: auth.owner_account_id,
                        course_id: course.id,
                        group_formation_id: courseGroupFormation.id,
                    })
                );
                const timestamp = new Date().getTime();
                setSurvey(
                    Object.assign(
                        {},
                        {
                            id: uuid.v4(),
                            active: true,
                            created_on: timestamp,
                            modified_on: timestamp,
                            criteria: [],
                        }
                    )
                );

                const questions = courseGroupFormation.criteria.map((criterion) => {
                    switch (criterion.id) {
                        case 'country': {
                            criterion.options = [...COUNTRY_LIST.map(({ name, code }) => ({ key: code, value: name }))];
                            return criterion;
                        }

                        case 'time-zone': {
                            criterion.options = [...TIME_ZONE_LIST.map(({ text }) => ({ key: text, value: text }))];
                            return criterion;
                        }

                        case 'gender': {
                            criterion.options = [
                                ...PRONOUN_OPTIONS.map((option) => ({
                                    key: option.value,
                                    value: option.title,
                                })),
                            ];
                            return criterion;
                        }

                        case 'english-proficiency': {
                            criterion.options = [
                                ...EN_PROFICIENCY_OPTIONS.map((option) => ({
                                    key: option.value,
                                    value: option.title,
                                })),
                            ];
                            return criterion;
                        }

                        default:
                            return criterion;
                    }
                });
                if (findMySurvey && findMySurvey.length > 0) {
                    const mySurvey = findMySurvey[0];
                    const checkMySurvey = mySurvey.owner_account_id === auth.owner_account_id;
                    if (checkMySurvey) {
                        setReview(true);
                        setQuestions(questions);
                        setSurvey(mySurvey);

                        return setCurrentStep(STEP_KEYS.FINISHED_SURVEY);
                    }
                } else {
                    setQuestions(questions);
                }
                setCurrentStep(STEP_KEYS.SURVEY);

                setLoading(false);
            };
            getData();
        }
    }, [auth, course]);

    return (
        <PageContext.Provider
            value={{
                auth,
                loading,
                renderQuestions,
                stepKeys: STEP_KEYS,
                currentStep,
                questions,
                canSubmit,
                review,
                handleClickNext,
                handleClickBack,
                handleClickToReview,
                handleClickToCourseDashboard,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
